import React, { useContext, useEffect } from 'react';
import { createSelector } from 'reselect';
import { find, pick, isNil } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import Notifier from 'common/helpers/Notifier';
import { SEARCH_CLIENTS, SEARCH_CONTEXTS, SEARCH_ROLES } from '@unite-us/app-search';
import { getAuthToken } from 'src/api/config';
import {
  hasPaymentsUserAccess,
  hasCaseManagerRole,
  hint716SearchNetworkHubSupportPremiumSelector,
  includePathwaysServices,
  hint1246HideLgbtqPlusFilter as hint1246HideLgbtqPlusFilterSelector,
  cerb1485Ny1115Networks as cerb1485Ny1115NetworksSelector,
  cerb1519ScreeningRequiredFeeSchedules as cerb1519ScreeningRequiredFeeSchedulesSelector,
  cerb1486Ny1115ClientSocialCareCoverageIds as cerb1486Ny1115ClientSocialCareCoverageIdsSelector,
} from 'common/utils/FeatureFlags/flags';
import { env } from 'src/config/env';
import { TrackerContext } from '@unite-us/client-utils';
import { ComponentLibraryLoader } from '@unite-us/app-components';
import { updateGlobalState } from 'actions/Global/globalActions';

const SearchApp = ComponentLibraryLoader({
  loadFn: () => import('@unite-us/app-search'),
  component: 'SearchApp',
  loadingApp: 'app-client',
  source: 'app-search',
  datadogRum,
});

const ShareDrawerLoader = ComponentLibraryLoader({
  loadFn: () => import('@unite-us/shares-utils'),
  component: 'ShareDrawer',
  loadingApp: 'app-client',
  source: 'shares-utils',
  datadogRum,
});

function getMyNetworkFilters({
  employeeId,
  networkId,
  providerId,
}) {
  return {
    'provider.networks': networkId,
    referable: {
      employee: employeeId,
      network: networkId,
      provider: providerId,
    },
  };
}

function getReferralFilters({
  referral,
  employeeId,
  contact,
}) {
  if (!referral) return {};
  return {
    services: referral.serviceId,
    'provider.licensed': referral.isLicensed,
    ...(!isNil(referral.providerSensitive) && { 'provider.sensitive': referral.providerSensitive }),
    ...(referral.isLicensed ? {
      receiving_referrals: true,
    } : { 'provider.networks': referral.toNetworkId }),
    referable: {
      employee: employeeId,
      network: referral.fromNetworkId,
      person: referral.personId,
      provider: referral.providerId,
      recipient_network: referral.toNetworkId,
    },
    contact: contact ?
      pick(
        contact,
        [
          'first_name',
          'last_name',
          'phone_numbers',
          'email_addresses',
          'date_of_birth',
          'gender',
          'addresses',
          'household',
        ],
      ) :
      undefined,
  };
}

function getInitialFilters({
  networkId,
  providerId,
  referral,
  employeeId,
  isPaymentsUserRole,
  contact,
}) {
  const baseFilters = {
    active: true,
  };
  const additionalFilters = networkId ?
    getMyNetworkFilters({
      employeeId,
      networkId,
      providerId,
      isPaymentsUserRole,
    }) :
    getReferralFilters({
      referral,
      employeeId,
      contact,
      isPaymentsUserRole,
    });

  return {
    ...baseFilters,
    ...additionalFilters,
  };
}

const SearchPrograms = ({
  activeNetworks,
  includePathways,
  serviceTypeOptions,
  employeeId,
  employeeNetworks,
  providerId,
  allAddresses,
  networkId,
  referral,
  userCoordinates,
  isPaymentsUserRole,
  isCaseManagerRole,
  contact,
  isShoppingCartOpen,
  setIsShoppingCartOpen,
  shoppingCartBtnRef,
  screeningRequiredFeeSchedulesIds,
  ny1115SocialCoverageIds,
  hint716SearchNetworkHubSupportPremium,
  hint1246HideLgbtqPlusFilter,
  cerb1485Ny1115Networks,
}) => {
  useEffect(() => {
    setIsShoppingCartOpen(false);
  }, []);

  const initialFilters = getInitialFilters({
    networkId,
    providerId,
    referral,
    employeeId,
    isPaymentsUserRole,
    contact,
  });
  const trackEvent = useContext(TrackerContext);

  serviceTypeOptions.sort((a, b) => {
    if (a.parent_code < b.parent_code) return -1;
    if (a.parent_code > b.parent_code) return 1;
    return a.name.localeCompare(b.name);
  });

  const networkName = activeNetworks.filter((n) => n.id === networkId).name;

  const userRoles = [
    ...isPaymentsUserRole ? [SEARCH_ROLES.PAYMENTS] : [],
    ...isCaseManagerRole ? [SEARCH_ROLES.CASE_MANAGER] : [],
  ];

  return (
    <SearchApp
      callbacks={{
        trackEvent,
        notify: {
          error: (message) => Notifier.dispatch('error', message),
          success: (message) => {
            Notifier.dispatch('success', message);
          },
          warn: (message) => Notifier.dispatch('warning', message),
        },
      }}
      serviceTypeOptions={serviceTypeOptions}
      allAddresses={allAddresses}
      // TODO: this prop can be remove if the service type is looked inside the serviceTypeOptions
      referralServiceType={referral && {
        id: referral.serviceId,
        name: referral.serviceName,
      }}
      networkName={networkName}
      userNetworks={employeeNetworks}
      cerb1485Ny1115Networks={cerb1485Ny1115Networks}
      appSettings={
        {
          client: SEARCH_CLIENTS.APP_CLIENT,
          components: { ShareDrawer: ShareDrawerLoader },
          context: referral ? SEARCH_CONTEXTS.REFERRAL : SEARCH_CONTEXTS.MY_NETWORK,
          env: {
            getAuthToken,
            employeeId,
            providerId,
            googleApiKey: env.GOOGLE_MAPS_API_KEY,
            SHARES_URL: env.SHARES_URL,
          },
          endpoints: {
            search: {
              url: env.SEARCH_API,
            },
            core: {
              url: env.CORE_API,
            },
          },
          flags: {
            hint716SearchNetworkHubSupportPremium,
            serviceTypesIncludePathways: includePathways,
            hint1246HideLgbtqPlusFilter,
          },
          roles: userRoles,
          idsOfFeeSchedulesThatRequireSpecializedScreenings: screeningRequiredFeeSchedulesIds,
          ny1115SocialCoverageIds,
        }
      }
      initialFilters={initialFilters}
      userCoordinates={userCoordinates}
      isShoppingCartOpen={isShoppingCartOpen}
      setIsShoppingCartOpen={setIsShoppingCartOpen}
      shoppingCartBtnRef={shoppingCartBtnRef}
      setShoppingCartBtnRef={() => {}} // NetworkSubHeader takes care of setting the btn ref here
    />
  );
};

// Should match shape of address validator in Core
const addressShape = {
  address_type: PropTypes.string,
  line_1: PropTypes.string,
  line_2: PropTypes.string,
  city: PropTypes.string.isRequired,
  county: PropTypes.string,
  state: PropTypes.string.isRequired,
  postal_code: PropTypes.string,
  country: PropTypes.string.isRequired,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  is_primary: PropTypes.bool,
};

SearchPrograms.propTypes = {
  activeNetworks: PropTypes.arrayOf(PropTypes.object).isRequired,
  employeeId: PropTypes.string.isRequired,
  employeeNetworks: PropTypes.arrayOf(PropTypes.string).isRequired,
  providerId: PropTypes.string.isRequired,
  contact: PropTypes.object,
  allAddresses: PropTypes.shape({
    CLIENT: PropTypes.arrayOf(PropTypes.shape(addressShape)),
    USER: PropTypes.arrayOf(PropTypes.shape(addressShape)),
    GROUP: PropTypes.arrayOf(PropTypes.shape(addressShape)),
  }).isRequired,
  includePathways: PropTypes.bool.isRequired,
  networkId: PropTypes.string.isRequired,
  referral: PropTypes.shape({
    fromNetworkId: PropTypes.string,
    isLicensed: PropTypes.bool,
    personId: PropTypes.string,
    providerId: PropTypes.string,
    serviceId: PropTypes.string,
    serviceName: PropTypes.string,
    toNetworkId: PropTypes.string,
  }),
  serviceTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      parent_id: PropTypes.string,
      parent_code: PropTypes.string,
      name: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      taxonomy: PropTypes.string.isRequired,
      is_sensitive: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  userCoordinates: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }).isRequired,
  isShoppingCartOpen: PropTypes.bool.isRequired,
  setIsShoppingCartOpen: PropTypes.func.isRequired,
  shoppingCartBtnRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }), // necessary for superset, triggers error in old experience
  screeningRequiredFeeSchedulesIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  ny1115SocialCoverageIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  isPaymentsUserRole: PropTypes.bool.isRequired,
  isCaseManagerRole: PropTypes.bool.isRequired,
  hint716SearchNetworkHubSupportPremium: PropTypes.bool.isRequired,
  hint1246HideLgbtqPlusFilter: PropTypes.bool.isRequired,
  cerb1485Ny1115Networks: PropTypes.arrayOf(PropTypes.string).isRequired,
};

SearchPrograms.defaultProps = {
  referral: undefined,
  shoppingCartBtnRef: undefined,
  contact: {},
};

function mapStateToProps(state, ownProps) {
  const activeNetworks = state.globalState.activeNetworks;
  const employeeNetworks = state.globalState.activeNetworks.map((network) => network.id);
  const employeeId = state.globalState.currentEmployee.id;
  const providerId = state.globalState.currentEmployee.provider.id;
  const includePathways = includePathwaysServices(state);
  const { referral } = ownProps;
  const contact = find(state.contacts.contacts, { id: referral?.personId });
  const selectServiceTypes = createSelector(
    (inState) => inState.session.globals.service_types,
    (serviceTypes) => {
      const ret = [];
      serviceTypes.forEach((parentType) => {
        parentType.children.forEach((child) => (
          ret.push({
            id: child.id,
            parent_id: parentType.id,
            parent_code: parentType.code,
            name: child.name,
            code: child.code,
            taxonomy: child.taxonomy,
            is_sensitive: child.sensitive,
          })
        ));

        ret.push({
          id: parentType.id,
          parent_id: null,
          parent_code: null,
          name: parentType.name,
          code: parentType.code,
          taxonomy: parentType.taxonomy,
          is_sensitive: parentType.sensitive,
        });
      });
      return ret;
    },
  );

  const {
    currentEmployee: {
      addresses: userAddresses,
      provider: {
        addresses: groupAddresses,
      },
    },
  } = state.globalState;
  const userCoordinates = Object.keys(state.session.position.geoCoordinates).length === 0 ?
    state.session.position.ipCoordinates : state.session.position.geoCoordinates;
  const isShoppingCartOpen = state.globalState.isShoppingCartOpen;

  return {
    activeNetworks,
    employeeNetworks,
    includePathways,
    serviceTypeOptions: selectServiceTypes(state),
    employeeId,
    providerId,
    isPaymentsUserRole: hasPaymentsUserAccess(state),
    isCaseManagerRole: hasCaseManagerRole(state),
    contact,
    allAddresses: {
      client: contact?.addresses,
      user: userAddresses,
      ours: groupAddresses,
    },
    userCoordinates,
    isShoppingCartOpen,
    screeningRequiredFeeSchedulesIds: cerb1519ScreeningRequiredFeeSchedulesSelector(state),
    ny1115SocialCoverageIds: cerb1486Ny1115ClientSocialCareCoverageIdsSelector(state),
    hint716SearchNetworkHubSupportPremium: hint716SearchNetworkHubSupportPremiumSelector(state),
    hint1246HideLgbtqPlusFilter: hint1246HideLgbtqPlusFilterSelector(state),
    cerb1485Ny1115Networks: cerb1485Ny1115NetworksSelector(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setIsShoppingCartOpen: (isOpen) => dispatch(updateGlobalState({ isShoppingCartOpen: isOpen })),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchPrograms);
